// SPS tenant dark theme — colors sourced from Figma "Southern Pipe" design file (SPS Dark mode).
import { createTheme } from '@mui/material/styles';
import './tokens';

const spsDarkTheme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'dark',
    primary: {
      // Brand Vermillion (PMS 179C) per SPS Brand Guidelines — was the Figma coral #F75E56.
      main: '#E4392F',
      contrastText: '#ffffff',
    },
    navigation: {
      background:        '#3d434b',
      subPanelBg:        '#464d57',
      divider:           'rgba(255, 255, 255, 0.12)',
      // was #f75e56 — 3.14:1 behind the white NavBar pill label. Now 5.03:1.
      tabSelectedBg:     '#D22F23',
      tabSelectedBorder: 'transparent',
      // Selected rows keep white subPanelText: 5.03:1.
      itemSelectedBg:    '#D22F23',
      // was #ff9a94 — 4.19:1 on subPanelBg. Now 5.01:1.
      link:              '#ffb3ae',
      badge:             '#f73a5b',
      text:              '#ffffff',
      textMuted:         'rgba(255, 255, 255, 0.65)',
      subPanelText:      '#ffffff',
      subPanelTextMuted: 'rgba(255, 255, 255, 0.65)',
      subPanelAvatarBg:  'rgba(255, 255, 255, 0.2)',
    },
    surface: {
      appBackground:    '#464d57',
      chatPanelBg:      '#464d57',
      chatHeaderBg:     '#464d57',
      inputBg:          '#515964',
      inputBorder:      'rgba(255, 255, 255, 0.18)',
      inputBorderFocus: '#E4392F',
      inputText:        'rgba(255, 255, 255, 0.85)',
      divider:          'rgba(255, 255, 255, 0.08)',
      primaryText:      '#ffffff',
      // was 0.40 — 2.81:1 against #464d57. Now 5.37:1.
      secondaryText:    'rgba(255, 255, 255, 0.72)',
      emptyStateIcon:   'rgba(255, 255, 255, 0.40)',
      toastBg:          '#ffffff',
      toastText:        '#2a2e42',
    },
    chat: {
      // Brand Vermillion (PMS 179C) — was the Figma coral #f75e56.
      userBubbleBg:          '#E4392F',
      userBubbleText:        '#ffffff',
      assistantBubbleBg:     '#464d57',
      assistantBubbleBorder: 'rgba(255, 255, 255, 0.10)',
      bubbleText:            '#ffffff',
      // was 0.40 — 2.81:1 against #464d57. Now 5.37:1.
      sentStatus:            'rgba(255, 255, 255, 0.72)',
      // 4.93:1 on the assistant bubble (#464d57).
      mentionText:           '#FFB74D',
      reactionAction:        'rgba(255, 255, 255, 0.60)',
      reactionActionHover:   '#ffffff',
      systemBubbleBg:        'rgba(255, 255, 255, 0.06)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.ndm-compose-input-wrapper': {
          background: '#ffffff !important',
        },
        '.ndm-compose-option-avatar': {
          backgroundColor: '#d9dce0 !important',
          color: '#2a2e42 !important',
        },
        '.ndm-compose-option-name': {
          color: '#181d27 !important',
        },
        '.ndm-compose-option-email': {
          color: '#8e97a3 !important',
        },
        '.ai-message .message, .assistant-message': {
          background: 'var(--mui-palette-chat-assistantBubbleBg) !important',
          borderColor: 'var(--mui-palette-chat-assistantBubbleBorder) !important',
          color: 'var(--mui-palette-chat-bubbleText)',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          color: '#2a2e42',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          color: '#1A1F2E',
          '& .MuiTypography-root': { color: '#1A1F2E' },
          '& .MuiInputLabel-root': { color: '#6B7280' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#f7433a' },
          '& .MuiInputBase-input': { color: '#1A1F2E' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d9dce0' },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8e97a3' },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f7433a' },
          '& .MuiButton-text': { color: '#1A1F2E' },
          '& .MuiButton-text:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
          '& .MuiDivider-root': { borderColor: 'rgba(0,0,0,0.12)' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1A1F2E',
        },
      },
    },
    // See darkTheme.ts — same gap, same reasoning: Popover was the one overlay
    // surface still inheriting MUI's dark paper under light-mode text colours.
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          color: '#1A1F2E',
          '& .MuiDivider-root': { borderColor: 'rgba(0,0,0,0.12)' },
          '& .MuiButton-text': { color: '#1A1F2E' },
          '& .MuiListItemButton-root:hover, & .MuiMenuItem-root:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
          '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(0,0,0,0.08)' },
        },
      },
    },
  },
  typography: {
    // SPS brand font (guideline) — Montserrat. Other tenants keep Proxima Nova.
    fontFamily: "'Montserrat', sans-serif",
    // Heavy headers stand in for the guideline's Avenir Next Heavy (Montserrat-only decision).
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 800 },
    h4: { fontWeight: 800 },
    h5: { fontWeight: 800 },
    h6: { fontWeight: 800 },
  },
});

export default spsDarkTheme;
